import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PreviewModes } from '@vp/react-product-preview';
import { FlexBox, SquareImageContainer, useScreenClass } from '@vp/swan';
import { ViewLargerImagesModal } from './ViewLargerImagesModal';
import { ProductImagePreview } from './ProductImagePreview';

/**
 * Intentionally *not* using LineItemDataProvider to decouple this component from that contract
 *
 * Responsible for rendering image preview and "view larger" icon /modal. Component renders purcsData when available and falls back to livePreviewUrl
 */

export const ItemPreviewDisplay = ({
  productName,
  productKey,
  productVersion,
  livePreviewUrl,
  documentRefUrl,
  productAttributeList,
  useSquareDimensionsForPurcs,
  hideMagnifyingGlass = false,
}) => {
  const screenClass = useScreenClass();
  const [isViewLargerModalOpen, setIsViewLargerModalOpen] = useState(false);

  // PURCs required params: https://gitlab.com/vistaprint-org/design-technology/scenes/purcs-react#required-parameters
  const hasRequiredPurcsData = !!(
    productKey &&
    productAttributeList &&
    documentRefUrl
  );

  const isMobile = screenClass === 'xs' || screenClass === 'sm';

  // Standardizes padding between magnifying glass and image when rendering static image vs. PurcsPreview
  const magnifyingGlassPadding = !hasRequiredPurcsData && !isMobile ? 7 : 0;

  if (!hasRequiredPurcsData && !livePreviewUrl) {
    return null;
  }

  if (useSquareDimensionsForPurcs) {
    return (
      <>
        <SquareImageContainer data-testid="square-image-container">
          <ProductImagePreview
            previewMode={PreviewModes.FirstPageOnly}
            documentRefUrl={documentRefUrl}
            productAttributeList={productAttributeList}
            livePreviewUrl={livePreviewUrl}
            hasRequiredPurcsData={hasRequiredPurcsData}
            productKey={productKey}
            productVersion={productVersion}
            onClick={() => setIsViewLargerModalOpen(true)}
          />
        </SquareImageContainer>
        <FlexBox alignItems="center" flexDirection="column" pt={3}>
          <ViewLargerImagesModal
            productName={productName}
            isOpen={isViewLargerModalOpen}
            onZoomButtonClick={() => setIsViewLargerModalOpen(true)}
            onDismiss={() => setIsViewLargerModalOpen(false)}
          >
            <ProductImagePreview
              previewMode={PreviewModes.Carousel}
              documentRefUrl={documentRefUrl}
              productAttributeList={productAttributeList}
              livePreviewUrl={livePreviewUrl}
              hasRequiredPurcsData={hasRequiredPurcsData}
              productKey={productKey}
              productVersion={productVersion}
            />
          </ViewLargerImagesModal>
        </FlexBox>
      </>
    );
  }

  return (
    <>
      <ProductImagePreview
        previewMode={
          isMobile ? PreviewModes.FirstPageOnly : PreviewModes.Carousel
        }
        documentRefUrl={documentRefUrl}
        productAttributeList={productAttributeList}
        livePreviewUrl={livePreviewUrl}
        hasRequiredPurcsData={hasRequiredPurcsData}
        productKey={productKey}
        productVersion={productVersion}
        onClick={() => setIsViewLargerModalOpen(true)}
      />
      {!hideMagnifyingGlass && (
        <FlexBox
          alignItems="center"
          flexDirection="column"
          pt={magnifyingGlassPadding}
        >
          <ViewLargerImagesModal
            productName={productName}
            isOpen={isViewLargerModalOpen}
            onZoomButtonClick={() => setIsViewLargerModalOpen(true)}
            onDismiss={() => setIsViewLargerModalOpen(false)}
          >
            <ProductImagePreview
              previewMode={PreviewModes.Carousel}
              documentRefUrl={documentRefUrl}
              productAttributeList={productAttributeList}
              livePreviewUrl={livePreviewUrl}
              hasRequiredPurcsData={hasRequiredPurcsData}
              productKey={productKey}
              productVersion={productVersion}
            />
          </ViewLargerImagesModal>
        </FlexBox>
      )}
    </>
  );
};

// Exports a memoized version of this component so it will only re-render when its props change.
// This prevents the PURCS modal from unnecessarily re-painting on every re-render (nice!)
export const MemoizedItemPreviewDisplay = React.memo(ItemPreviewDisplay);

ItemPreviewDisplay.propTypes = {
  productName: PropTypes.string,
  productKey: PropTypes.string,
  productVersion: PropTypes.number,
  livePreviewUrl: PropTypes.string,
  documentRefUrl: PropTypes.string,
  productAttributeList: PropTypes.array,
  useSquareDimensionsForPurcs: PropTypes.bool,
  hideMagnifyingGlass: PropTypes.bool,
};
